import {useTheme, Switch, FormControlLabel, Box, Typography} from "@mui/material";
import {observer} from 'mobx-react';
import CreateOrderFeature from "./CreateOrderFeature";
import {useNavigate} from "react-router-dom";
import * as React from "react";
import {styles} from "./CreateOrderStyles";
import {useApplication} from "../../../ApplicationProvider";
import {useNavigatorFeature} from "../../../useFeature";
import LayoutContentWrapper from "../../../components/layout/LayoutContentWrapper";
import FilledButton from "../../../components/buttons/FilledButton";
import LayoutContentTitle from "../../../components/layout/LayoutContentTitle";
import FormRow from "../../../forms/FormRow";
import FormSection from "../../../forms/FormSection";
import FormAutocompleteField from "../../../components/fields/FormAutocompleteField";
import FormTextField from "../../../components/fields/FormTextField";
import FormColumn from "../../../forms/FormColumn";
import FormDateField from "../../../components/fields/FormDateField";
import {useState} from "react";
import {ORDERS_PATH} from "../../../routes";
import CustomerDebtAlertBanner from "../CustomerDebtAlertBanner";
import OrdersWithSamePurchaseOrderNumberAndDeliveryDateModal
    from "./OrdersWithSamePurchaseOrderNumberAndDeliveryDateModal";

function CreateOrderScreen() {
    const application = useApplication();
    const theme = useTheme();
    const navigator = useNavigate();
    const feature = useNavigatorFeature(() => new CreateOrderFeature(application, navigator), navigator);
    const style = styles(theme);
    const [confirmRedirectOpen, setConfirmRedirectOpen] = useState(false);

    const isFeatureReady = (feature) => {
        return feature && feature.loaded
    }

    async function hasOrdersWithSamePurchaseOrderNumberAndDeliverySite() {
        if (!feature.purchaseOrderNumberField()) {
            feature.createOrder();
            return;
        }
        await feature.ordersForSamePurchaseOrderNumberAndDeliverySite();
        if (feature.hasOrdersWithSamePurchaseOrderNumberAndDeliverySite()) {
            setConfirmRedirectOpen(true);
        } else {
            feature.createOrder();
        }
    }

    const customerForm = () => {
        return (
            <FormSection title="Cliente">
            <FormColumn>
                <FormRow>
                    <FormAutocompleteField label={'Razón social'} field={feature.form.getFieldByName("businessName")}
                                           options={feature.businessNameOptions()}
                                           clear={() => feature.clearFormFields()}/>
                    <FormAutocompleteField label={'Número de cliente'}
                                           field={feature.form.getFieldByName("customerCode")}
                                           options={feature.customerCodeOptions()}
                                           clear={() => feature.clearFormFields()}/>
                    <FormAutocompleteField label={'Nombre de fantasía'}
                                           field={feature.form.getFieldByName("fantasyName")}
                                           options={feature.fantasyNameOptions()}
                                           clear={() => feature.clearFormFields()}/>
                </FormRow>
            </FormColumn>
            </FormSection>
        )
    }

    const isDeliveryDateDisabled = () => {
        return !feature.deliverySiteFieldsAreReady()
    }

    const isDateDisabledForDelivery = (aDateTime) => {
        return !feature.isDateValidForDelivery(aDateTime)
    }

    const handleCancelOrder = () => {
        setConfirmRedirectOpen(false);
        navigator(ORDERS_PATH);
    };

    const handleCreateOrder = () => {
        setConfirmRedirectOpen(false)
        feature.createOrder()
    };

    const deliverySiteForm = () => {
        return (
            <FormSection title="Punto de Entrega">
                <FormColumn>
                    <FormRow>
                        <FormAutocompleteField label={'Punto de entrega'}
                                               field={feature.form.getFieldByName("deliverySiteName")}
                                               options={feature.deliverySiteNameOptions()}
                                               clear={() => feature.clearDeliverySite()}/>
                        <FormAutocompleteField label={'Dirección'}
                                               field={feature.form.getFieldByName("deliverySiteAddress")}
                                               options={feature.deliverySiteAddressOptions()}/>
                        <FormAutocompleteField label={'Localidad'}
                                               field={feature.form.getFieldByName("deliverySiteCity")}
                                               options={feature.deliverySiteCityOptions()}/>
                    </FormRow>
                </FormColumn>
            </FormSection>
        )
    }
    const purchaseOrderNumberForm = () => {
        return (
            <FormSection title="Número de orden de compra">
            <FormColumn>
                <FormRow>
                    <FormTextField label={'Número de orden de compra'}
                                   field={feature.form.getFieldByName("purchaseOrderNumber")}
                                   onBlur={() => feature.getEnabledDatesForDeliverySiteWithPurchaseOrderNumber()}
                    />
                </FormRow>
            </FormColumn>
            </FormSection>
        )
    }

    const freeOrderForm = () => {
        return (
            <FormSection title="Tipo de pedido">
                <FormControlLabel
                    control={<Switch checked={feature.isFree}
                                     onChange={(event) => feature.setIsFree(event.target.checked)}
                                     sx={{
                                         '& .MuiSwitch-switchBase.Mui-checked': {
                                             color: theme.palette.warning.main,
                                         },
                                         '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                             backgroundColor: theme.palette.warning.main,
                                         },
                                     }}/>}
                    label="Pedido sin cargo"
                />
                {feature.isFree && (
                    <Box sx={style.freeOrderCommentContainer}>
                        <FormRow>
                            <FormTextField label={'Comentarios'}
                                           field={feature.form.getFieldByName("observations")}
                                           placeholder={'Escriba su comentario aquí...'}
                                           rows={4}/>
                        </FormRow>
                    </Box>
                )}
            </FormSection>
        )
    }

    const deliveryDateForm = () => {
        return (
            <FormSection title="Fecha de entrega">
                <FormRow>
                    <FormDateField label={'Fecha de entrega'} field={feature.form.getFieldByName("deliveryDate")}
                                   disabled={isDeliveryDateDisabled()}
                                   isDateDisabled={(date) => isDateDisabledForDelivery(date)}/>
                </FormRow>
            </FormSection>
        )
    }

    const createOrderContent = () => {
        return isFeatureReady(feature) && (
            <div>
                <div style={style.titleContainer}>
                    <LayoutContentTitle title="Crear Pedido"/>
                    <FilledButton onClick={() => feature.clearFormFields()}>
                        Borrar filtros
                    </FilledButton>
                    {/*<div style={style.datesRowContainer}>*/}
                    {/*    <span><Typography>Fecha de Creación: {feature.creationDate()}</Typography></span>*/}
                    {/*    <span><Typography>Última Edición: {feature.lastEditionDate()}</Typography></span>*/}
                    {/*</div>*/}
                </div>

                {feature.customerDebtAlert && <CustomerDebtAlertBanner debtAlert={feature.customerDebtAlert}/>}
                {customerForm()}
                {deliverySiteForm()}
                {freeOrderForm()}
                {purchaseOrderNumberForm()}
                {deliveryDateForm()}

                <div style={style.buttonsContainer}>
                    <FilledButton disabled={!feature.canCreateOrder()} onClick={hasOrdersWithSamePurchaseOrderNumberAndDeliverySite}>
                        Crear Pedido
                    </FilledButton>
                    {!feature.hasRequiredObservations() && (
                        <Typography variant="body2" color="error">
                            Un pedido sin cargo no se puede crear sin comentario.
                        </Typography>
                    )}
                </div>
                <OrdersWithSamePurchaseOrderNumberAndDeliveryDateModal
                    open={confirmRedirectOpen}
                    onConfirm={handleCreateOrder}
                    onCancel={handleCancelOrder}
                    feature={feature}
                    setConfirmRedirectOpen={setConfirmRedirectOpen}
                />
            </div>
        )
    }

    return (
        <LayoutContentWrapper isLoading={!isFeatureReady(feature)} renderContent={() => createOrderContent()}/>
    );
}

export default observer(CreateOrderScreen);
